import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Building2,
  Search,
  Calendar,
  ArrowUpDown,
  X as XIcon,
  Phone,
  Globe,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { brandAssets } from "@/data/brand";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUSES = ["new", "in_progress", "done", "archived"];

// ─── Helpers ───────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("lt-LT", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const inRange = (iso, from, to) => {
  if (!iso) return false;
  const d = new Date(iso).getTime();
  if (from && d < new Date(from).getTime()) return false;
  if (to && d > new Date(to).getTime() + 24 * 3600 * 1000 - 1) return false;
  return true;
};

const matches = (row, q, keys) => {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  return keys.some((k) => (row[k] || "").toString().toLowerCase().includes(needle));
};

// ─── UI atoms ──────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, tid }) {
  return (
    <div className="card-mt p-6" data-testid={tid}>
      <Icon size={22} className="text-brand-lime mb-3" />
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-1">{label}</p>
      <p className="h-display text-4xl text-white">{value}</p>
    </div>
  );
}

function StatusSelect({ value, onChange, disabled, statusLabels, testid }) {
  const s = STATUSES.includes(value) ? value : "new";
  const styles = {
    new: "border-brand-lime/40 text-brand-lime bg-brand-lime/[0.08]",
    in_progress: "border-amber-500/40 text-amber-300 bg-amber-500/[0.08]",
    done: "border-zinc-500/40 text-zinc-200 bg-zinc-500/[0.08]",
    archived: "border-zinc-700 text-zinc-500 bg-zinc-800/40",
  };
  return (
    <select
      value={s}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testid}
      className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand-lime disabled:opacity-60 cursor-pointer ${styles[s]}`}
    >
      {STATUSES.map((v) => (
        <option key={v} value={v} className="bg-black text-white">{statusLabels[v]}</option>
      ))}
    </select>
  );
}

function FilterBar({ q, setQ, from, setFrom, to, setTo, status, setStatus, statusLabels, onClear, testidPrefix, placeholder }) {
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-stretch">
      <div className="md:col-span-5 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={`${testidPrefix}-search`} className="input-mt pl-9 !py-2.5 text-sm" />
      </div>
      <div className="md:col-span-2 relative">
        <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${testidPrefix}-from`} className="input-mt pl-9 !py-2.5 text-xs font-mono uppercase" aria-label="From" />
      </div>
      <div className="md:col-span-2 relative">
        <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${testidPrefix}-to`} className="input-mt pl-9 !py-2.5 text-xs font-mono uppercase" aria-label="To" />
      </div>
      <div className="md:col-span-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`${testidPrefix}-status`} className="input-mt !py-2.5 text-xs font-mono uppercase">
          <option value="all">— ALL —</option>
          {STATUSES.map((s) => (<option key={s} value={s}>{statusLabels[s]}</option>))}
        </select>
      </div>
      <button type="button" onClick={onClear} data-testid={`${testidPrefix}-clear`} className="md:col-span-1 btn-outline !py-2.5 text-[10px] font-mono uppercase tracking-widest justify-center" title="Clear">
        <XIcon size={12} /> Clear
      </button>
    </div>
  );
}

function SortableTh({ label, k, sort, setSort }) {
  const active = sort.key === k;
  return (
    <th onClick={() => setSort((prev) => ({ key: k, dir: prev.key === k && prev.dir === "desc" ? "asc" : "desc" }))}
        className={`text-left px-4 py-3 font-mono uppercase text-[10px] tracking-widest cursor-pointer select-none whitespace-nowrap ${active ? "text-brand-lime" : "text-zinc-400 hover:text-zinc-200"}`}
        data-testid={`th-${k}`}>
      <span className="inline-flex items-center gap-1.5">
        {label}
        <ArrowUpDown size={11} className={active ? "opacity-100" : "opacity-40"} />
        {active && <span className="text-[9px]">{sort.dir === "desc" ? "↓" : "↑"}</span>}
      </span>
    </th>
  );
}

// ─── Panels ────────────────────────────────────────────────────────
function useStatusUpdater(kind, setRows) {
  const { t } = useI18n();
  const [pendingId, setPendingId] = useState(null);
  const update = async (id, next) => {
    setPendingId(id);
    try {
      const r = await api.patch(`/admin/${kind}/${id}/status`, { status: next });
      setRows((prev) => prev.map((x) => (x.id === id ? { ...x, status: r.data.status } : x)));
      toast.success(t("admin.dashboard.statusUpdated"));
    } catch {
      toast.error(t("admin.dashboard.statusFailed"));
    } finally {
      setPendingId(null);
    }
  };
  return { pendingId, update };
}

function ContactsPanel({ rows, setRows, statusLabels }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const { pendingId, update } = useStatusUpdater("contacts", setRows);

  const filtered = useMemo(() => {
    let out = rows.filter((r) =>
      matches(r, q, ["name", "email", "phone", "vehicle", "engine", "registration", "service", "message"]) &&
      (status === "all" || (r.status || "new") === status) &&
      (from || to ? inRange(r.created_at, from, to) : true)
    );
    out = [...out].sort((a, b) => {
      const va = (a[sort.key] || "").toString();
      const vb = (b[sort.key] || "").toString();
      return sort.dir === "desc" ? vb.localeCompare(va) : va.localeCompare(vb);
    });
    return out;
  }, [rows, q, from, to, status, sort]);

  const clear = () => { setQ(""); setFrom(""); setTo(""); setStatus("all"); };

  return (
    <div>
      <FilterBar
        q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo}
        status={status} setStatus={setStatus} statusLabels={statusLabels}
        onClear={clear} testidPrefix="contacts-filter"
        placeholder="Search name, email, phone, vehicle, service…"
      />

      <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-zinc-500">
        <span data-testid="contacts-count">{filtered.length} / {rows.length}</span>
      </div>

      {/* Desktop */}
      <div className="mt-3 hidden md:block overflow-x-auto rounded-xl border border-zinc-800" data-testid="table-contacts">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 border-b border-zinc-800">
            <tr>
              <SortableTh label="Data" k="created_at" sort={sort} setSort={setSort} />
              <SortableTh label="Vardas" k="name" sort={sort} setSort={setSort} />
              <SortableTh label="Kontaktas" k="email" sort={sort} setSort={setSort} />
              <SortableTh label="Automobilis" k="vehicle" sort={sort} setSort={setSort} />
              <SortableTh label="Paslauga" k="service" sort={sort} setSort={setSort} />
              <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-widest text-zinc-400">Žinutė</th>
              <SortableTh label={t("admin.dashboard.updateStatus")} k="status" sort={sort} setSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id || i} className="border-b border-zinc-900 hover:bg-zinc-950/60 align-top">
                <td className="px-4 py-3 text-zinc-300 whitespace-nowrap font-mono text-xs">{fmt(r.created_at)}</td>
                <td className="px-4 py-3 text-white font-medium">{r.name || "—"}</td>
                <td className="px-4 py-3 text-zinc-300">
                  <div className="text-xs">{r.email}</div>
                  <div className="text-xs text-zinc-500">{r.phone}</div>
                </td>
                <td className="px-4 py-3 text-zinc-300">
                  <div className="text-xs">{r.vehicle || "—"}</div>
                  {r.engine && <div className="text-[11px] text-zinc-500 font-mono">{r.engine}</div>}
                  {r.registration && <div className="text-[11px] text-zinc-500 font-mono">{r.registration}</div>}
                </td>
                <td className="px-4 py-3 text-zinc-300 text-xs max-w-[180px]"><div className="line-clamp-2">{r.service || "—"}</div></td>
                <td className="px-4 py-3 text-zinc-400 text-xs max-w-[280px]"><div className="line-clamp-3 whitespace-pre-wrap break-words">{r.message || "—"}</div></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <StatusSelect value={r.status} onChange={(v) => update(r.id, v)} disabled={pendingId === r.id} statusLabels={statusLabels} testid={`status-contact-${r.id}`} />
                    {pendingId === r.id && <Loader2 size={13} className="animate-spin text-brand-lime" />}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (<tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">{t("admin.dashboard.noData")}</td></tr>)}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="mt-3 md:hidden space-y-3" data-testid="cards-contacts">
        {filtered.map((r, i) => (
          <div key={r.id || i} className="card-mt card-mt-pad">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div><p className="text-white font-medium">{r.name}</p><p className="text-[11px] text-zinc-500 font-mono">{fmt(r.created_at)}</p></div>
              <StatusSelect value={r.status} onChange={(v) => update(r.id, v)} disabled={pendingId === r.id} statusLabels={statusLabels} testid={`status-contact-mobile-${r.id}`} />
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2 text-zinc-300"><Mail size={12} className="text-brand-lime shrink-0" /> {r.email}</div>
              <div className="flex items-center gap-2 text-zinc-300"><Phone size={12} className="text-brand-lime shrink-0" /> {r.phone}</div>
              {r.vehicle && <div className="text-zinc-400"><span className="text-zinc-500">Automobilis:</span> {r.vehicle} {r.engine ? `· ${r.engine}` : ""}</div>}
              {r.service && <div className="text-zinc-400"><span className="text-zinc-500">Paslauga:</span> {r.service}</div>}
              {r.message && <div className="text-zinc-400 whitespace-pre-wrap"><span className="text-zinc-500">Žinutė:</span> {r.message}</div>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (<p className="text-zinc-500 py-8 text-center font-mono uppercase text-xs tracking-widest">{t("admin.dashboard.noData")}</p>)}
      </div>
    </div>
  );
}

function PartnersPanel({ rows, setRows, statusLabels }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const { pendingId, update } = useStatusUpdater("partners", setRows);

  const filtered = useMemo(() => {
    let out = rows.filter((r) =>
      matches(r, q, ["business_name", "contact_person", "email", "phone", "city", "business_type", "website", "message"]) &&
      (status === "all" || (r.status || "new") === status) &&
      (from || to ? inRange(r.created_at, from, to) : true)
    );
    out = [...out].sort((a, b) => {
      const va = (a[sort.key] || "").toString();
      const vb = (b[sort.key] || "").toString();
      return sort.dir === "desc" ? vb.localeCompare(va) : va.localeCompare(vb);
    });
    return out;
  }, [rows, q, from, to, status, sort]);

  const clear = () => { setQ(""); setFrom(""); setTo(""); setStatus("all"); };

  return (
    <div>
      <FilterBar
        q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo}
        status={status} setStatus={setStatus} statusLabels={statusLabels}
        onClear={clear} testidPrefix="partners-filter"
        placeholder="Search business, city, contact, email…"
      />

      <div className="mt-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-zinc-500">
        <span data-testid="partners-count">{filtered.length} / {rows.length}</span>
      </div>

      <div className="mt-3 hidden md:block overflow-x-auto rounded-xl border border-zinc-800" data-testid="table-partners">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 border-b border-zinc-800">
            <tr>
              <SortableTh label="Data" k="created_at" sort={sort} setSort={setSort} />
              <SortableTh label="Įmonė" k="business_name" sort={sort} setSort={setSort} />
              <SortableTh label="Kontaktas" k="contact_person" sort={sort} setSort={setSort} />
              <SortableTh label="Miestas" k="city" sort={sort} setSort={setSort} />
              <SortableTh label="Tipas" k="business_type" sort={sort} setSort={setSort} />
              <th className="text-left px-4 py-3 font-mono uppercase text-[10px] tracking-widest text-zinc-400">Žinutė</th>
              <SortableTh label={t("admin.dashboard.updateStatus")} k="status" sort={sort} setSort={setSort} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id || i} className="border-b border-zinc-900 hover:bg-zinc-950/60 align-top">
                <td className="px-4 py-3 text-zinc-300 whitespace-nowrap font-mono text-xs">{fmt(r.created_at)}</td>
                <td className="px-4 py-3 text-white font-medium">
                  {r.business_name}
                  {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="block text-[11px] text-brand-lime hover:underline mt-0.5">{r.website.replace(/^https?:\/\//, "")}</a>}
                </td>
                <td className="px-4 py-3 text-zinc-300">
                  <div className="text-xs">{r.contact_person}</div>
                  <div className="text-xs text-zinc-500">{r.email}</div>
                  <div className="text-xs text-zinc-500">{r.phone}</div>
                </td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{r.city || "—"}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">
                  <div>{r.business_type || "—"}</div>
                  {r.monthly_volume && <div className="text-[11px] text-zinc-500 font-mono">{r.monthly_volume}/mo</div>}
                </td>
                <td className="px-4 py-3 text-zinc-400 text-xs max-w-[260px]"><div className="line-clamp-3 whitespace-pre-wrap break-words">{r.message || "—"}</div></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <StatusSelect value={r.status} onChange={(v) => update(r.id, v)} disabled={pendingId === r.id} statusLabels={statusLabels} testid={`status-partner-${r.id}`} />
                    {pendingId === r.id && <Loader2 size={13} className="animate-spin text-brand-lime" />}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (<tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">{t("admin.dashboard.noData")}</td></tr>)}
          </tbody>
        </table>
      </div>

      <div className="mt-3 md:hidden space-y-3" data-testid="cards-partners">
        {filtered.map((r, i) => (
          <div key={r.id || i} className="card-mt card-mt-pad">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div><p className="text-white font-medium">{r.business_name}</p><p className="text-[11px] text-zinc-500 font-mono">{fmt(r.created_at)} · {r.city}</p></div>
              <StatusSelect value={r.status} onChange={(v) => update(r.id, v)} disabled={pendingId === r.id} statusLabels={statusLabels} testid={`status-partner-mobile-${r.id}`} />
            </div>
            <div className="space-y-1.5 text-xs">
              <div className="text-zinc-300">{r.contact_person}</div>
              <div className="flex items-center gap-2 text-zinc-300"><Mail size={12} className="text-brand-lime shrink-0" /> {r.email}</div>
              <div className="flex items-center gap-2 text-zinc-300"><Phone size={12} className="text-brand-lime shrink-0" /> {r.phone}</div>
              {r.website && (<div className="flex items-center gap-2 text-brand-lime"><Globe size={12} className="shrink-0" /><a href={r.website} target="_blank" rel="noreferrer" className="hover:underline truncate">{r.website.replace(/^https?:\/\//, "")}</a></div>)}
              <div className="text-zinc-400"><span className="text-zinc-500">Tipas:</span> {r.business_type}{r.monthly_volume ? ` · ${r.monthly_volume}/mo` : ""}</div>
              {r.message && (<div className="text-zinc-400 whitespace-pre-wrap flex gap-1.5"><MessageSquare size={12} className="text-zinc-600 mt-0.5 shrink-0" />{r.message}</div>)}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (<p className="text-zinc-500 py-8 text-center font-mono uppercase text-xs tracking-widest">{t("admin.dashboard.noData")}</p>)}
      </div>
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [stats, setStats] = useState({ contacts: 0, partners: 0 });
  const [contacts, setContacts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusLabels = t("admin.dashboard.statuses");

  useEffect(() => {
    const token = localStorage.getItem("mt_admin_token");
    if (!token) { nav("/admin/login", { replace: true }); return; }
    Promise.all([api.get("/admin/stats"), api.get("/admin/contacts"), api.get("/admin/partners")])
      .then(([s, c, p]) => {
        setStats({ contacts: s.data.contacts, partners: s.data.partners });
        setContacts(c.data);
        setPartners(p.data);
      })
      .catch(() => {
        localStorage.removeItem("mt_admin_token");
        nav("/admin/login", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("mt_admin_token");
    localStorage.removeItem("mt_admin_email");
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen">
      <header className="border-b border-zinc-900 sticky top-0 z-40 bg-black/80 backdrop-blur-xl">
        <div className="container-mt flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={brandAssets.logoWordmark} alt="MAD TURBO" className="h-7" />
            <span className="text-xs font-mono uppercase tracking-widest text-brand-lime border-l border-zinc-800 pl-3">ADMIN</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-outline text-xs py-2">
            <LogOut size={14} /> {t("admin.dashboard.logout")}
          </button>
        </div>
      </header>

      <main className="container-mt py-10">
        <h1 className="h-display text-4xl md:text-5xl text-white mb-8">{t("admin.dashboard.title")}</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <StatCard icon={Mail} label={t("admin.dashboard.contacts")} value={stats.contacts} tid="stat-contacts" />
          <StatCard icon={Building2} label={t("admin.dashboard.partners")} value={stats.partners} tid="stat-partners" />
        </div>

        <Tabs defaultValue="contacts">
          <TabsList className="bg-zinc-950 border border-zinc-800">
            <TabsTrigger value="contacts" data-testid="tab-contacts">{t("admin.dashboard.contacts")}</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">{t("admin.dashboard.partners")}</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            <ContactsPanel rows={contacts} setRows={setContacts} statusLabels={statusLabels} />
          </TabsContent>
          <TabsContent value="partners">
            <PartnersPanel rows={partners} setRows={setPartners} statusLabels={statusLabels} />
          </TabsContent>
        </Tabs>

        {loading && <p className="text-zinc-500 mt-6" data-testid="admin-loading">{t("common.loading")}</p>}
      </main>
    </div>
  );
}
